import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { formatOccupantsShort, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { pluralizeToString } from 'lib/string/pluralize'
import { formatGuestsDisplay } from 'lib/string/stringUtils'
import React, { useMemo } from 'react'
import moment from 'moment'
import useDefaultAirport from 'hooks/useDefaultAirport'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import TextButton from 'LuxKit/Button/TextButton'
import config from 'constants/config'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { useOfferPriceWithMember } from 'hooks/Offers/useOfferPrice'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { connect } from 'react-redux'
import { EmptyArray, unique } from 'lib/array/arrayUtils'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import { useDirectSearchPrices } from 'hooks/Search/useSearchPrices'
import offerPageURL from 'lib/offer/offerPageURL'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsMultiRoomEnabled } from 'selectors/featuresSelectors'
import { useCharterFlightPrice } from 'hooks/useFlightPrice'
import { getCharterBundleRoute } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { FlightsClassTypes } from 'constants/flight'
import useIsSearchPage from 'hooks/Navigation/useIsSearchPage'
import { useMaldivesBundleContext } from 'components/Pages/HotelSearchPage/MaldivesBundleContext'

interface MappedProps {
  canViewLuxPlusBenefits: boolean
}

interface Props extends MappedProps {
  offer: App.Offer | App.OfferSummary
  filters?: App.OfferListFilters
  rooms?: Array<App.Occupants>
  pkg: App.Package
  pricing?: App.OfferAvailableRate
  hasDates?: boolean
  offerUrl?: string
}

function SearchTileFlightOnlyPricing(props: Props) {
  const {
    offer,
    pkg,
    pricing,
    hasDates,
    rooms = EmptyArray,
    offerUrl,
    canViewLuxPlusBenefits,
    filters,
  }: Props = props

  // Use bundle filters if this offer is a charter offer, otherwise use original filters
  const { bundleFilters, isBundleMode } = useMaldivesBundleContext()
  const isMultiRoomEnable = useAppSelector(state => getIsMultiRoomEnabled(state, offer))
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const isSearchPage = useIsSearchPage()

  // Use bundle filters if this offer is a charter bundle offer and bundle mode is enabled
  const shouldUseBundleFilters = !!(isBundleMode && bundleFilters && charterBundleRoute)

  // Use bundle filters when in bundle mode, otherwise use original filters
  const bundleSearchPrices = useDirectSearchPrices({ filters: bundleFilters ?? {}, offerId: offer.id })
  const searchPrices = useDirectSearchPrices({ filters: filters ?? {}, offerId: offer.id })

  // Use bundle pricing only when available to avoid mixing search durations/prices
  const directSearchPrices = shouldUseBundleFilters ? bundleSearchPrices : searchPrices
  const shouldFetchCharterFlightPrice = !!(isSearchPage && charterBundleRoute && bundleFilters?.checkIn && bundleFilters?.checkOut)

  // Use useCharterFlightPrice to get flight price for Maldives charter bundle
  const [charterFlightPrice] = useCharterFlightPrice({
    charterBundleRoute,
    checkInDate: bundleFilters?.checkIn,
    checkOutDate: bundleFilters?.checkOut,
    occupancies: rooms ?? [],
    fareClass: FlightsClassTypes.ECONOMY,
    disabled: !shouldFetchCharterFlightPrice,
  })

  // Fallback to offer.flightPrices if charter flight price is not available
  const defaultAirport = useDefaultAirport()
  const fallbackFlightPrice = (offer.flightPrices[defaultAirport?.code ?? ''] || 0)
  const flightPrice = (charterBundleRoute && charterFlightPrice > 0) ? charterFlightPrice : fallbackFlightPrice
  // Calculate totalGuests from rooms
  const totalGuests = useMemo(() => {
    if (rooms.length === 0) return undefined
    return sumUpOccupancies(rooms)
  }, [rooms])

  const guestsDisplayText = useMemo(() => {
    if (!isBundleMode || !totalGuests) return undefined
    return formatGuestsDisplay(totalGuests)
  }, [isBundleMode, totalGuests])

  const shouldShowPerPersonPrice = !!(isSearchPage && charterBundleRoute && totalGuests && totalGuests > 0)
  const perPersonDivisor = shouldShowPerPersonPrice ? totalGuests : 1

  const bundleDuration = useMemo(() => {
    if (!bundleFilters?.checkIn || !bundleFilters?.checkOut) return undefined
    return moment(bundleFilters.checkOut).diff(bundleFilters.checkIn, 'days')
  }, [bundleFilters?.checkIn, bundleFilters?.checkOut])

  const bundlePackage = useMemo(() => {
    if (!shouldUseBundleFilters || !bundleDuration || !offer.packages) return undefined
    return offer.packages.find(currentPackage => currentPackage.duration === bundleDuration)
  }, [shouldUseBundleFilters, bundleDuration, offer.packages])

  const resolvedPackage = bundlePackage ?? pkg
  const resolvedPricing = bundlePackage ? undefined : pricing

  const priceMultiplier = rooms?.length || 1
  const extraGuestSurcharges = useMemo(() => getSumExtraGuestsSurcharge(rooms || [], offer, resolvedPackage), [resolvedPackage, rooms, offer])

  const {
    price = directSearchPrices?.lowestPrice ?? 0,
    memberPrice = directSearchPrices?.lowestMemberPrice ?? 0,
    taxesAndFees = 0,
    propertyFees = 0,
  } = resolvedPricing ?? resolvedPackage

  const showPriceDetails = taxesAndFees > 0 || propertyFees > 0 || config.agentHub.isEnabled
  let hotelPrice = price * priceMultiplier + extraGuestSurcharges + flightPrice
  const showMemberPrice = memberPrice > 0
  const rateOrPkgMemberPrice = resolvedPricing?.memberPriceWithSurcharge ?? resolvedPackage.memberPrice
  const hotelMemberPrice = showMemberPrice ? rateOrPkgMemberPrice * priceMultiplier + extraGuestSurcharges + flightPrice : 0
  let totalPrice = hotelPrice + propertyFees
  let totalMemberPrice = showMemberPrice ? hotelMemberPrice + propertyFees : 0
  const pkgValueOrMemberValue = canViewLuxPlusBenefits ? resolvedPackage.memberValue : resolvedPackage.value
  let totalValue = pkgValueOrMemberValue * priceMultiplier + extraGuestSurcharges + flightPrice
  let shouldShowValue = resolvedPackage.shouldDisplayValue && pkgValueOrMemberValue > 0 && totalValue >= (showMemberPrice ? totalMemberPrice : totalPrice)

  if (directSearchPrices) {
    // search does not know anything about flight prices, so we just add them in to our pricing
    hotelPrice = (directSearchPrices.lowestPrice ?? 0) + flightPrice
    totalPrice = hotelPrice // we already include property fees in the direct search prices
    totalMemberPrice = showMemberPrice ? (directSearchPrices.lowestMemberPrice ?? 0) + flightPrice : 0
    totalValue = (directSearchPrices.lowestPriceValue ?? 0) + flightPrice
    shouldShowValue = resolvedPackage.shouldDisplayValue && pkgValueOrMemberValue > 0 && totalValue >= (showMemberPrice ? totalMemberPrice : totalPrice)
  }

  const valueOffPercent = getValueOffPercent(totalValue, showMemberPrice ? hotelMemberPrice : hotelPrice)

  const displayTotalPrice = perPersonDivisor > 1 ? totalPrice / perPersonDivisor : totalPrice
  const displayTotalMemberPrice = perPersonDivisor > 1 ? totalMemberPrice / perPersonDivisor : totalMemberPrice
  const priceSaleUnit = shouldShowPerPersonPrice ? 'person' : 'total'

  // https://aussiecommerce.atlassian.net/browse/CONLT-863
  // Only show discount pill for forced bundles
  const shouldShowRate = !offer.forceBundleId && offer.bundledWithFlightsOnly && valueOffPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  const {
    total: displayPrice,
    memberTotal: displayMemberPrice,
    saleUnit: displaySaleUnit,
  } = useOfferPriceWithMember({
    value: undefined,
    saleUnit: priceSaleUnit,
    duration: resolvedPackage.duration,
    total: displayTotalPrice,
    memberTotal: displayTotalMemberPrice,
  })

  const priceCaption = useMemo(() => {
    const roomCount = rooms.length
    const roomText = isMultiRoomEnable && roomCount > 1 ? `, ${pluralizeToString('room', roomCount)}` : ''
    const hasFlightPrice = flightPrice > 0
    const flightSuffix = hasFlightPrice ? ' & flights' : ', hotel only'

    if (shouldUseBundleFilters && bundleDuration) {
      return `${pluralizeToString('night', bundleDuration)}${roomText}${flightSuffix}, ${formatOccupantsShort(rooms)}`
    }
    if (directSearchPrices?.duration) {
      return `${pluralizeToString('night', directSearchPrices.duration)}${roomText}${flightSuffix}, ${formatOccupantsShort(rooms)}`
    }
    else if (hasDates) {
      return `${pluralizeToString('night', resolvedPackage.duration)}${roomText} & flights ${`, ${formatOccupantsShort(rooms)}`}`
    } else {
      if (offer.tileDurationLabel) {
        return `${offer.tileDurationLabel.toLocaleLowerCase()}${roomText} & flights from`
      }
      return 'Hotel & flights from'
    }
  }, [rooms, isMultiRoomEnable, shouldUseBundleFilters, bundleDuration, directSearchPrices?.duration, hasDates, resolvedPackage.duration, offer.tileDurationLabel, flightPrice])

  const offerLocations = useMemo(() => unique(offer.locations.concat(offer.location)),
    [offer.locations, offer.location])
  const vendorName = offer.vendorName

  return <div>
    <PriceRowPriceCaption>
      {priceCaption}
    </PriceRowPriceCaption>
    <LuxPlusPriceStack
      price={displayPrice}
      memberPrice={displayMemberPrice}
      size="L"
      saleUnit={displaySaleUnit}
      testid="search-price"
      offerConfig={offer.luxPlus}
    />
    <PriceRowTaxesAndFees />
    {shouldShowValue && <PriceRowValueDiscountWithCaption
      data-testid="value-box"
      size="M"
      originalValue={totalValue}
      discountPercentage={shouldShowRate ? valueOffPercent : undefined}
    />}
    {guestsDisplayText && <PriceRowCaption>{guestsDisplayText}</PriceRowCaption>}
    <PriceRowAgentHubCommission
      productType={offer.productType}
      offerId={offer.id}
      vendorName={vendorName}
      offerLocations={offerLocations}
      packageId={pkg.id}
    />
    {showPriceDetails && <OfferPriceDetailsRow
      trigger="price-row"
      triggerSize="M"
      offer={offer}
      duration={resolvedPackage.duration}
      propertyFees={propertyFees}
      hotelPrice={hotelPrice}
      rooms={rooms}
      dueAtPropertyMessage={offer.property?.taxesAndFeesContent ?? ''}
      hotelMemberPrice={hotelMemberPrice}
      showMemberPrice={showMemberPrice}
      context="search"
      cta={
        <TextButton kind="primary" fit="flex" to={offerUrl ?? offerPageURL(offer)}>
          View Offer
        </TextButton>
      }
    />}
  </div>
}

const mapStateToProps = (state: App.State): MappedProps => ({
  canViewLuxPlusBenefits: getCanViewLuxPlusBenefits(state),
})

export default connect(mapStateToProps)(SearchTileFlightOnlyPricing)
